/**
 * UpdatesManagerCard (#1875, Feature Spotlight part 1) - admin console
 * section for product-update rows: add / edit / enable / delete. Rows live
 * in the tips table as `kind: "update"` and are read from the same
 * GET /api/admin/tips response the Tips card uses. English fields only;
 * translation happens server-side on save through PUT /api/admin/tips/{id}.
 *
 * `publishedAt` is the ordering key and the seen-state watermark for the
 * login modal (part 2, #1876). The form takes it in the browser's local
 * time (same convention as the exports card) and sends a UTC instant.
 *
 * The enabled toggle round-trips the full English copy, which re-translates
 * the row - the same accepted cost as the Tips card (no partial update).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Stack,
} from '@mui/material';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { apiGlobal } from '../../services/api';
import { useToast } from '../ToastNotification';
import { SectionContainer } from '../ds';

const errMessage = (err) =>
  err?.body?.error || err?.body?.message || err?.message || 'Something went wrong';

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);

const pad = (n) => String(n).padStart(2, '0');

// ISO instant -> the value a datetime-local input wants, in local time.
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// datetime-local value (local wall time) -> UTC instant for the API.
const toInstant = (local) => {
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const formatWhen = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unpublished';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const emptyForm = () => ({
  tipId: '', title: '', body: '', linkPath: '', publishedAt: toLocalInput(null), enabled: true, isNew: true,
});

const UpdatesManagerCard = () => {
  const { showSuccess, showError } = useToast();
  const [updates, setUpdates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiGlobal.get('/admin/tips');
      setUpdates(res.updates || []);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveUpdate = async () => {
    const tipId = form.isNew ? slugify(form.tipId || form.title) : form.tipId;
    if (!tipId) { showError('An update id is required'); return; }
    const publishedAt = toInstant(form.publishedAt);
    if (!publishedAt) { showError('A published date and time is required'); return; }
    setBusy(true);
    try {
      await apiGlobal.put(`/admin/tips/${encodeURIComponent(tipId)}`, {
        kind: 'update',
        title: form.title.trim(),
        body: form.body.trim(),
        linkPath: form.linkPath.trim() || null,
        publishedAt,
        enabled: form.enabled,
      });
      showSuccess(`Saved ${tipId} (translated to all locales)`);
      setForm(null);
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (row) => {
    const en = row.copy?.['en-US'] || {};
    setBusy(true);
    try {
      await apiGlobal.put(`/admin/tips/${encodeURIComponent(row.tipId)}`, {
        kind: 'update',
        title: en.title || '',
        body: en.body || '',
        linkPath: row.linkPath || null,
        publishedAt: row.publishedAt,
        enabled: !row.enabled,
      });
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteUpdate = async (tipId) => {
    setBusy(true);
    try {
      await apiGlobal.delete(`/admin/tips/${encodeURIComponent(tipId)}`);
      showSuccess(`Deleted ${tipId}`);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  return (
    <SectionContainer
      title="Updates"
      category="planned"
      count={updates.length}
      onAdd={() => setForm(emptyForm())}
    >
      <Stack spacing={1} sx={{ pt: 1 }}>
        {updates.length === 0 && (
          <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>
            No updates published.
          </Typography>
        )}
        {updates.map((row) => (
          <Box
            key={row.tipId}
            data-testid={`update-row-${row.tipId}`}
            sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}
          >
            <Switch
              size="small"
              checked={!!row.enabled}
              onChange={() => toggleEnabled(row)}
              disabled={busy}
              slotProps={{ input: { 'aria-label': `enable ${row.tipId}` } }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }} noWrap>
                {row.copy?.['en-US']?.title || row.tipId}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }} noWrap>
                {row.tipId} · {formatWhen(row.publishedAt)}{row.linkPath ? ` · ${row.linkPath}` : ''}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={`edit ${row.tipId}`}
              disabled={busy}
              onClick={() => setForm({
                tipId: row.tipId,
                title: row.copy?.['en-US']?.title || '',
                body: row.copy?.['en-US']?.body || '',
                linkPath: row.linkPath || '',
                publishedAt: toLocalInput(row.publishedAt),
                enabled: !!row.enabled,
                isNew: false,
              })}
              sx={{ color: 'text.secondary', p: '2px' }}
            >
              <IconPencil size={15} />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`delete ${row.tipId}`}
              disabled={busy}
              onClick={() => setConfirmDelete(row.tipId)}
              sx={{ color: 'text.secondary', p: '2px' }}
            >
              <IconTrash size={15} />
            </IconButton>
          </Box>
        ))}
      </Stack>

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.isNew ? 'New update' : `Edit ${form?.tipId}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {form?.isNew && (
              <TextField
                size="small" label="Update id (slug, auto from title if blank)"
                value={form?.tipId || ''}
                onChange={(e) => setForm((f) => ({ ...f, tipId: e.target.value }))}
              />
            )}
            <TextField
              size="small" label="Title (English)"
              value={form?.title || ''}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              size="small" label="Body (English)" multiline minRows={3}
              value={form?.body || ''}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />
            <TextField
              size="small" label="Link path (optional, e.g. /snapshots)"
              value={form?.linkPath || ''}
              onChange={(e) => setForm((f) => ({ ...f, linkPath: e.target.value }))}
            />
            <TextField
              size="small" label="Published (local time)" type="datetime-local" sx={{ width: 260 }}
              slotProps={{ inputLabel: { shrink: true } }}
              value={form?.publishedAt || ''}
              onChange={(e) => setForm((f) => ({ ...f, publishedAt: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={saveUpdate} disabled={busy}>
            Save and translate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => !busy && setConfirmDelete(null)}>
        <DialogTitle>Delete update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Delete ${confirmDelete}? It leaves the updates page and the login announcement for everyone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
          <Button color="error" onClick={() => deleteUpdate(confirmDelete)} disabled={busy}>Delete</Button>
        </DialogActions>
      </Dialog>
    </SectionContainer>
  );
};

export default UpdatesManagerCard;
